/**
 * ZeichnungsberechtigtePage - Administration › Zeichnungsberechtigte Personen (F1-3).
 *
 * WHAT F1 IS, and why this page is the point of it.
 *
 * Build plan PART 2B, F1: a ZB person is **not a user** and may sign without any Brokly
 * account - the Verwaltungsratspräsident who signs has no login and never will. Wave 3 built
 * that model correctly (no `app_user` link anywhere) and shipped the api. It shipped with **no
 * screen at all**, so the only way to add a signatory was an api call, which means in practice
 * there were none.
 *
 * WHAT IS AND IS NOT HERE. This is the maintenance surface: add a person, edit them, upload
 * their PNG, deactivate them. The **selection popup** that lets a broker pick two of them for a
 * non-standardized contract is still not built (`pending_tests` F1-1), because the document
 * generator it would insert them into does not exist yet - building a picker that feeds nothing
 * would be worse than naming the gap.
 *
 * THERE IS NO DELETE, deliberately. A signature already placed into an issued contract has to
 * stay explicable afterwards, so removal is deactivation and the row survives.
 *
 * THE SIGNATURE PREVIEW uses `GET /admin/signatories/:id/signature` (wave 13), which reads the
 * bytes back by the persisted url and **re-verifies the checksum recorded at upload**. Before
 * that route existed, nothing had ever read a stored signature back, so nobody knew whether the
 * stored PNG was still usable. The preview is therefore not decoration: it is the first place
 * a corrupted upload becomes visible, which is exactly what F1-4 was worried about.
 *
 * [Source: apps/web/src/features/administration/pages/AusschlusslistePage.tsx] (page shape)
 * [Source: apps/api/src/modules/mandate/broker-signatory.controller.ts]
 * [Source: 2026-08-11_stefan-feedback-build-plan.md] (PART 2B F1)
 */

import { useEffect, useRef, useState } from 'react';
import { AlertCircle, Loader2, Plus, Upload } from 'lucide-react';
import { apiClient } from '../../../lib/api-client';
import type { BrokerSignatoryWire } from '../../../lib/api-client';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { Button, TextInput } from '../components';
import { Badge } from '../../../components/ui/Badge';

const meta = ADMIN_PAGE_META.zeichnungsberechtigte;

/** One row, with its signature preview and the actions that apply to it. */
function SignatoryRow({
  row,
  onChanged,
  onError,
}: {
  row: BrokerSignatoryWire;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  // Bumped after an upload so the browser refetches instead of showing the cached old image.
  const [version, setVersion] = useState(0);

  async function handleUpload(file: File) {
    setBusy(true);
    try {
      await apiClient.signatories.uploadSignature(row.id, file, file.name);
      setVersion((v) => v + 1);
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Der Upload ist fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleActive() {
    setBusy(true);
    try {
      await apiClient.signatories.update(row.id, { isActive: !row.isActive });
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Die Änderung ist fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="flex items-center gap-4 border-b px-4 py-3 last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          {row.firstName} {row.lastName}
        </p>
        {row.roleLabel !== null && (
          <p className="truncate text-xs text-muted-foreground">{row.roleLabel}</p>
        )}
      </div>

      {!row.isActive && <Badge variant="secondary">Deaktiviert</Badge>}

      {/* The preview is the only place a corrupted upload becomes visible (F1-4). */}
      <div className="flex h-10 w-32 items-center justify-center rounded border bg-muted/40">
        {row.hasSignature ? (
          <img
            src={`${apiClient.signatories.signatureUrl(row.id)}?v=${version}`}
            alt={`Unterschrift von ${row.firstName} ${row.lastName}`}
            className="max-h-9 max-w-30 object-contain"
          />
        ) : (
          <span className="text-xs text-muted-foreground">Keine Unterschrift</span>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/png"
        className="hidden"
        aria-label={`Unterschrift für ${row.firstName} ${row.lastName} hochladen`}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleUpload(file);
          e.target.value = '';
        }}
      />

      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={busy}
        onClick={() => fileRef.current?.click()}
      >
        {busy ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
        PNG
      </Button>

      {/* Deactivate, never delete: an issued contract has to stay explicable. */}
      <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={handleToggleActive}>
        {row.isActive ? 'Deaktivieren' : 'Aktivieren'}
      </Button>
    </li>
  );
}

export function ZeichnungsberechtigtePage() {
  const [rows, setRows] = useState<BrokerSignatoryWire[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [roleLabel, setRoleLabel] = useState('');
  const [creating, setCreating] = useState(false);

  function load() {
    // Inactive rows are included: an admin has to be able to see and reactivate somebody they
    // deactivated, which is the whole reason deactivation replaced deletion.
    apiClient.signatories
      .list(true)
      .then((res) => {
        // Defensive: the api is typed to return a bare array, but a shape drift (e.g. a
        // `{ items: [...] }` wrapper) must never white-screen this page with `rows.map is
        // not a function`. Anything that is not an array degrades to an empty list.
        setRows(Array.isArray(res) ? res : []);
        setError(null);
      })
      .catch(() => setError('Die Liste konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (firstName.trim().length === 0 || lastName.trim().length === 0) {
      setError('Vorname und Nachname sind erforderlich.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      await apiClient.signatories.create({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        ...(roleLabel.trim() ? { roleLabel: roleLabel.trim() } : {}),
      });
      setFirstName('');
      setLastName('');
      setRoleLabel('');
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Die Person konnte nicht angelegt werden.');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <AdminPageHeader title={meta.title} description={meta.description} />

      <form onSubmit={handleCreate} className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-4">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Vorname</span>
          <TextInput
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            aria-label="Vorname"
            className="w-40"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Nachname</span>
          <TextInput
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            aria-label="Nachname"
            className="w-40"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Funktion (optional)</span>
          <TextInput
            value={roleLabel}
            onChange={(e) => setRoleLabel(e.target.value)}
            aria-label="Funktion"
            className="w-56"
          />
        </label>
        <Button type="submit" disabled={creating}>
          {creating ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
          Hinzufügen
        </Button>
      </form>

      {error !== null && (
        <p className="flex items-start gap-2 text-sm text-destructive" role="alert">
          <AlertCircle className="size-4 shrink-0" />
          {error}
        </p>
      )}

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Wird geladen …
        </p>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">
          Noch keine Zeichnungsberechtigten Personen erfasst. Eine solche Person braucht kein
          Benutzerkonto in Brokly.
        </p>
      ) : (
        <ul className="rounded-lg border bg-card" aria-label="Zeichnungsberechtigte Personen">
          {rows.map((r) => (
            <SignatoryRow key={r.id} row={r} onChanged={load} onError={setError} />
          ))}
        </ul>
      )}
    </div>
  );
}
